import { useEffect } from 'react';

interface PerformanceOptimizerProps {
  enableResourceHints?: boolean;
  enableCriticalCSS?: boolean;
}

const PerformanceOptimizer = ({ 
  enableResourceHints = true,
  enableCriticalCSS = true 
}: PerformanceOptimizerProps) => {
  useEffect(() => {
    if (enableResourceHints) {
      // Add resource hints for external domains
      const resourceHints = [
        { rel: 'dns-prefetch', href: 'https://fonts.googleapis.com' },
        { rel: 'dns-prefetch', href: 'https://fonts.gstatic.com' },
        { rel: 'dns-prefetch', href: 'https://analytics.tiktok.com' },
        { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
        { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossOrigin: 'anonymous' }
      ];

      resourceHints.forEach(({ rel, href, crossOrigin }) => {
        const existing = document.querySelector(`link[rel="${rel}"][href="${href}"]`);
        if (!existing) {
          const link = document.createElement('link');
          link.rel = rel;
          link.href = href;
          if (crossOrigin) link.crossOrigin = crossOrigin;
          document.head.appendChild(link);
        }
      });
    }

    // Optimize fonts loading and add critical CSS
    if (enableCriticalCSS) {
      const style = document.createElement('style');
      style.textContent = `
        /* Critical CSS for font display and fallbacks */
        body {
          font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }
        
        .font-roxborough,
        .font-andine {
          font-display: swap;
        }
        
        /* Prevent layout shift for hero section */
        .hero-container {
          min-height: 70vh;
        }
        
        /* Critical above-the-fold styles */
        .hero-gradient {
          background: linear-gradient(135deg, hsl(220 14% 96%) 0%, hsl(220 13% 91%) 100%);
        }
        
        @media (min-width: 768px) {
          .hero-container {
            min-height: 80vh;
          }
        }
        
        @media (prefers-color-scheme: dark) {
          .hero-gradient {
            background: linear-gradient(135deg, hsl(220 13% 18%) 0%, hsl(220 13% 13%) 100%);
          }
        }
      `;
      document.head.appendChild(style);
    }

    // Preload critical resources with higher priority for LCP optimization
    const criticalImages = [
      '/lovable-uploads/2bb14950-418a-4143-94a2-dfd749783abf.png', // Hero background - now preloaded in HTML head
      '/lovable-uploads/42367874-987e-4a41-a126-f72fb38bc740.png'  // Favicon/Logo
    ];

    criticalImages.forEach((src, index) => {
      const existing = document.querySelector(`link[rel="preload"][href="${src}"]`);
      if (!existing) {
        // Skip hero image since it's already preloaded in HTML head for better LCP
        if (index === 0) return;
        
        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'image';
        link.href = src;
        document.head.appendChild(link);
      }
    });

  }, [enableResourceHints, enableCriticalCSS]);

  return null;
};

export default PerformanceOptimizer;